import { createRouter, createWebHistory } from 'vue-router';

import Assignments from '@/views/Assignments.vue';
import Schedule from '@/views/Schedule.vue';
import Landing from '@/views/Landing.vue';
import Dashboard from '@/views/Dashboard.vue';
import Resources from '@/views/Resources.vue';
import NotFound from '@/views/404.vue';
import Privacy from '@/views/Privacy.vue';
import ClassPage from '@/views/ClassPage.vue';
import Terms from '@/views/Terms.vue';
import Stats from '@/views/Stats.vue';
import Banned from '@/views/Banned.vue';
import Menu from '@/views/Menu.vue';
import DataRequest from '@/views/DataRequest.vue';
import FactorGame from '@/views/FactorGame.vue';
import SpecialSchedule from '@/views/SpecialSchedule.vue';
import { consumeTokenFromUrl } from '@/config/auth';
import { getStoredToken, syncActiveSchool } from '@/config/schools';
import { fetchAdminRoles } from '@/config/adminApi';
import { fetchFacultyCapabilities } from '@/config/facultyApi';

// Remove Firebase Auth imports and code since we're using JWT tokens stored in localStorage

const routes = [
  {
    path: '/',
    name: 'Landing',
    component: Landing,
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: '/assignments',
    name: 'Assignments',
    component: Assignments,
    meta: { requiresAuth: true },
  },
  {
    path: '/schedule',
    name: 'Schedule',
    component: Schedule,
    meta: { requiresAuth: true },
  },
  {
    path: '/special',
    name: 'SpecialSchedule',
    component: SpecialSchedule,
    meta: { requiresAuth: true },
  },
  {
    path: '/resources',
    name: 'Resources',
    component: Resources,
    meta: { requiresAuth: true },
  },
  {
    path: '/class/:section_id',
    name: 'ClassPage',
    component: ClassPage,
    meta: { requiresAuth: true },
    props: true, // Pass route params as props to the component
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: Privacy,
    props: true, // Pass route params as props to the component
  },
  {
    path: '/terms',
    name: 'Terms',
    component: Terms,
    props: true, // Pass route params as props to the component
  },
  {
    path: '/stats',
    name: 'Stats',
    component: Stats,
    props: true, // Pass route params as props to the component
  },
  {
    // Legacy URL for the category compare tool, now inside the dashboard.
    path: '/classes',
    redirect: (to) => ({ path: '/admin/categories', query: to.query }),
  },
  {
    path: '/banned',
    name: 'Banned',
    component: Banned,
    props: true, // Pass route params as props to the component
  },
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'AdminOverview',
        component: () => import('@/views/admin/AdminOverview.vue'),
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/views/admin/AdminUsers.vue'),
        meta: { requiresSuperAdmin: true },
      },
      {
        path: 'users/:userId(\\d+)',
        name: 'AdminUserDetail',
        component: () => import('@/views/admin/AdminUserDetail.vue'),
        meta: { requiresSuperAdmin: true },
        props: true,
      },
      {
        path: 'status-bar',
        name: 'AdminStatusBar',
        component: () => import('@/views/admin/AdminStatusBar.vue'),
      },
      {
        path: 'digest',
        name: 'AdminDigest',
        component: () => import('@/views/admin/AdminDigest.vue'),
        meta: { requiresSuperAdmin: true },
      },
      {
        path: 'live-activities',
        name: 'AdminLiveActivities',
        component: () => import('@/views/admin/AdminLiveActivities.vue'),
        meta: { requiresSuperAdmin: true },
      },
      {
        path: 'mobile-digests',
        name: 'AdminMobileDigest',
        component: () => import('@/views/admin/AdminMobileDigest.vue'),
        meta: { requiresSuperAdmin: true },
      },
      {
        path: 'categories',
        name: 'AdminCategories',
        component: () => import('@/views/admin/AdminCategories.vue'),
        meta: { requiresSuperAdmin: true },
      },
      {
        path: 'schools',
        name: 'AdminSchools',
        component: () => import('@/views/admin/AdminSchools.vue'),
        meta: { requiresSuperAdmin: true },
      },
    ],
  },
  {
    path: '/faculty',
    component: () => import('@/views/faculty/FacultyLayout.vue'),
    meta: { requiresAuth: true, requiresFaculty: true },
    children: [
      {
        path: '',
        name: 'FacultyToday',
        component: () => import('@/views/faculty/FacultyToday.vue'),
      },
      {
        path: 'section/:sectionId',
        name: 'FacultySection',
        component: () => import('@/views/faculty/FacultySection.vue'),
        props: true,
      },
    ],
  },
    {
        path: '/menu',
        name: 'Menu',
        component: Menu,
        meta: { requiresAuth: true },
        props: true,
    },
  {
    path: '/data',
    name: 'DataRequest',
    component: DataRequest,
    meta: { requiresAuth: true },
  },
  {
    path: '/factor',
    name: 'FactorGame',
    component: FactorGame,
    // No auth required, accessible via direct URL only
  },
  {
    path: '/courses',
    name: 'CoursePlanner',
    component: () => import('@/views/CoursePlanner.vue'),
  },
  {
    path: '/:pathMatch(.*)*', // Catch-all route for undefined paths
    name: 'NotFound',
    component: NotFound,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation Guard
router.beforeEach(async (to) => {
  const tokenFromUrl = consumeTokenFromUrl();
  const token = tokenFromUrl || getStoredToken();
  syncActiveSchool({ token });
  const isAuthenticated = !!token;

  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);
  const requiresSuperAdmin = to.matched.some((record) => record.meta.requiresSuperAdmin);
  const requiresFaculty = to.matched.some((record) => record.meta.requiresFaculty);
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  if ((requiresAuth || requiresAdmin || requiresSuperAdmin || requiresFaculty) && !isAuthenticated) {
    return { path: '/', query: { loginRedirect: to.fullPath } };
  }

  let roles = { isAdmin: false, isSuperAdmin: false };
  let faculty = { isFaculty: false };
  if (isAuthenticated) {
    roles = await fetchAdminRoles();
    faculty = await fetchFacultyCapabilities();
  }

  if (requiresAdmin || requiresSuperAdmin) {
    if (requiresSuperAdmin && !roles.isSuperAdmin) {
      // School-admins hitting a superadmin section land on the overview.
      return roles.isAdmin ? '/admin' : '/';
    }
    return roles.isAdmin ? true : '/';
  }

  if (requiresFaculty && !faculty.isFaculty) {
    return '/dashboard';
  }

  const studentRouteNames = new Set([
    'Dashboard',
    'Assignments',
    'Schedule',
    'SpecialSchedule',
    'ClassPage',
  ]);
  if (faculty.isFaculty && studentRouteNames.has(to.name)) {
    return '/faculty';
  }

  if (requiresAuth) {
    return true;
  }

  // If the user is already logged in, prevent access to the landing page
  if (to.path === '/' && isAuthenticated) {
    return faculty.isFaculty ? '/faculty' : '/dashboard';
  }

  return true;
});

export default router;
